import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion, type Variants } from 'framer-motion';
import { AlertTriangle, X, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useT } from '@/i18n';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { toast } from '@/stores/toast.store';
import { usePatchSystemConfig } from '@/features/system-config/system-config.hooks';

/**
 * v0.6.0 N-Q6 safety modal — for edits to payroll-affecting keys:
 *   agent_tier_thresholds / uploader_tier_thresholds /
 *   agent_daily_target / uploader_daily_target
 *
 * UX gates required by the lock (frontend portion of 6 safeguards):
 *   #1 Red warning banner: "Cambiar estos valores afecta el cálculo
 *      de payroll (Cluster P). Coordiná con tu contador antes de
 *      guardar."
 *   #3 Double confirmation: 2 checkboxes both required — "Estoy
 *      seguro" + "Entiendo que esto afecta payroll"
 *
 * Backend enforces #2 (mandatory audit row), #4 (admin only), #5
 * (effective_date versioning). #6 (history CTA) is in SystemConfigPage.
 */
export interface ThresholdEditModalProps {
  open: boolean;
  configKey: string;
  currentValue: unknown;
  onClose: () => void;
}

export function ThresholdEditModal(props: ThresholdEditModalProps): JSX.Element {
  useBodyScrollLock(props.open);
  return createPortal(
    <AnimatePresence>
      {props.open ? <ThresholdEditModalInner {...props} /> : null}
    </AnimatePresence>,
    document.body,
  );
}

function ThresholdEditModalInner({ configKey, currentValue, onClose }: ThresholdEditModalProps): JSX.Element {
  const t = useT();
  const reduce = useReducedMotion();
  const mut = usePatchSystemConfig();

  const isTierObject = configKey.endsWith('_tier_thresholds');
  // v0.6.0 Fase 11.5 — replace JSON textarea with 4 labeled number inputs
  // for tier_thresholds. valueText still used for daily_target keys
  // (simple number input).
  const initialTier = (() => {
    const v = (currentValue ?? {}) as { base?: number; t1?: number; t2?: number; t3?: number };
    return {
      base: typeof v.base === 'number' ? String(v.base) : '',
      t1:   typeof v.t1   === 'number' ? String(v.t1)   : '',
      t2:   typeof v.t2   === 'number' ? String(v.t2)   : '',
      t3:   typeof v.t3   === 'number' ? String(v.t3)   : '',
    };
  })();
  const initialNumber = isTierObject ? '' : String(currentValue ?? '');

  const [tierDraft, setTierDraft] = useState(initialTier);
  const [valueText, setValueText] = useState<string>(initialNumber);
  const [reason, setReason] = useState<string>('');
  const today = new Date().toISOString().slice(0, 10);
  const [effectiveDate, setEffectiveDate] = useState<string>(today);
  const [ack1, setAck1] = useState(false);
  const [ack2, setAck2] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const canSubmit = reason.trim().length >= 5 && /^\d{4}-\d{2}-\d{2}$/.test(effectiveDate) && ack1 && ack2 && !mut.isPending;

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    let parsedValue: unknown;
    if (isTierObject) {
      const parseTier = (s: string): { value?: number; error?: string } => {
        const n = Number(s.trim());
        if (s.trim() === '' || !Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
          return { error: t('systemConfig.edit.tier.invalidNumber') };
        }
        return { value: n };
      };
      const b = parseTier(tierDraft.base);
      const t1 = parseTier(tierDraft.t1);
      const t2 = parseTier(tierDraft.t2);
      const t3 = parseTier(tierDraft.t3);
      if (b.error || t1.error || t2.error || t3.error) {
        setError(b.error || t1.error || t2.error || t3.error || '');
        return;
      }
      if (!(b.value! < t1.value! && t1.value! < t2.value! && t2.value! < t3.value!)) {
        setError(t('systemConfig.edit.tier.monotoneError'));
        return;
      }
      parsedValue = { base: b.value, t1: t1.value, t2: t2.value, t3: t3.value };
    } else {
      const n = Number(valueText);
      if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
        setError(t('systemConfig.edit.tier.invalidNumber'));
        return;
      }
      parsedValue = n;
    }
    try {
      const res = await mut.mutateAsync({
        key: configKey,
        body: { value: parsedValue, reason: reason.trim(), effectiveDate },
      });
      if ((res as { scheduled?: unknown })?.scheduled) {
        toast.success(t('systemConfig.editScheduled'));
      } else {
        toast.success(t('systemConfig.editSuccess'));
      }
      onClose();
    } catch (e) {
      setError((e as Error).message || 'Update failed');
    }
  };

  const backdrop: Variants = reduce
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 }, exit: { opacity: 1 } }
    : {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.2 } },
        exit: { opacity: 0, transition: { duration: 0.15 } },
      };
  const modal: Variants = reduce
    ? { hidden: { opacity: 1, scale: 1 }, visible: { opacity: 1, scale: 1 }, exit: { opacity: 1, scale: 1 } }
    : {
        hidden: { opacity: 0, scale: 0.96 },
        visible: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 300, damping: 25 } },
        exit: { opacity: 0, scale: 0.98, transition: { duration: 0.15 } },
      };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8">
      <motion.div
        className="absolute inset-0 bg-nav-navy/50 backdrop-blur-sm"
        variants={backdrop}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
        aria-hidden="true"
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        className="relative bg-white rounded-xl shadow-elevated w-full max-w-xl max-h-[90vh] overflow-y-auto font-body text-nav-navy"
        variants={modal}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <header className="sticky top-0 bg-white border-b border-nav-card px-5 py-3 flex items-start justify-between gap-3 z-10">
          <h2 className="font-head text-[15px] font-bold text-nav-navy">
            {t('systemConfig.edit.title')} <code className="text-xs bg-nav-hair px-1 py-0.5 rounded">{configKey}</code>
          </h2>
          <button type="button" onClick={onClose} aria-label={t('common.close')} className="text-nav-slate hover:text-nav-navy p-1 rounded">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {/* N-Q6 safeguard #1 — red warning banner */}
          <div className="flex gap-3 items-start rounded-md border-2 border-nav-red/40 bg-nav-redTint px-4 py-3">
            <AlertTriangle className="w-5 h-5 text-nav-red shrink-0 mt-0.5" aria-hidden />
            <div className="text-sm text-nav-red">
              <p className="font-semibold">{t('systemConfig.edit.warningTitle')}</p>
              <p className="mt-1">{t('systemConfig.edit.warningBody')}</p>
            </div>
          </div>

          {isTierObject ? (
            <div>
              <label className="block text-sm font-medium text-nav-navy mb-2">
                {t('systemConfig.edit.tier.label')}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(['base', 't1', 't2', 't3'] as const).map((tier) => {
                  const tierLabel = ({
                    base: t('tier.bronce'),
                    t1: t('tier.plata'),
                    t2: t('tier.oro'),
                    t3: t('tier.diamante'),
                  } as Record<string, string>)[tier];
                  return (
                    <label key={tier} className="block">
                      <span className="block text-xs font-medium text-nav-navy mb-1">{tierLabel}</span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={tierDraft[tier]}
                        onChange={(e) => setTierDraft({ ...tierDraft, [tier]: e.target.value })}
                        className="w-full font-mono tabular-nums rounded-md border border-nav-line px-3 py-2 focus:ring-2 focus:ring-nav-red focus:outline-none"
                        placeholder="0"
                      />
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-nav-slate mt-2">{t('systemConfig.edit.tier.monotoneHint')}</p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-nav-navy mb-1">
                {t('systemConfig.edit.dailyTarget.label')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={valueText}
                  onChange={(e) => setValueText(e.target.value)}
                  className="w-32 font-mono tabular-nums rounded-md border border-nav-line px-3 py-2 focus:ring-2 focus:ring-nav-red focus:outline-none"
                />
                <span className="text-sm text-nav-slate">{t('systemConfig.edit.dailyTarget.unit')}</span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-nav-navy mb-1">{t('systemConfig.edit.reasonLabel')} *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('systemConfig.edit.reasonPlaceholder')}
              rows={2}
              minLength={5}
              className="w-full text-sm rounded-md border border-nav-line px-3 py-2 focus:ring-2 focus:ring-nav-red focus:outline-none"
            />
            <p className="text-xs text-nav-slate mt-1">{t('systemConfig.edit.reasonHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-nav-navy mb-1">{t('systemConfig.edit.effectiveDateLabel')} *</label>
            <input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              className="rounded-md border border-nav-line px-3 py-2 focus:ring-2 focus:ring-nav-red focus:outline-none"
            />
            <p className="text-xs text-nav-slate mt-1">{t('systemConfig.edit.effectiveDateHint')}</p>
          </div>

          {/* N-Q6 safeguard #3 — double confirmation */}
          <div className="space-y-2 rounded-md bg-nav-softer border border-nav-card px-4 py-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={ack1} onChange={(e) => setAck1(e.target.checked)} className="mt-0.5" />
              <span className="text-sm text-nav-navy">{t('systemConfig.edit.ack1')}</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={ack2} onChange={(e) => setAck2(e.target.checked)} className="mt-0.5" />
              <span className="text-sm text-nav-navy">{t('systemConfig.edit.ack2')}</span>
            </label>
          </div>

          {error && (
            <div className="text-sm text-nav-red bg-nav-redTint border border-[#e3b5ae] rounded-md px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-nav-card px-5 py-3 flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={mut.isPending}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="danger" onClick={handleSubmit} disabled={!canSubmit}>
            {mut.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden />}
            {t('systemConfig.edit.submit')}
          </Button>
        </footer>
      </motion.div>
    </div>
  );
}
